import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

const comicMap = new Map<string, Comic>();
comics.forEach((c) => comicMap.set(c.id, c));

const categoryNameMap = new Map<string, string>();
categories.forEach((c) => categoryNameMap.set(c.slug, c.name));

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [showAllChapters, setShowAllChapters] = useState(false);

  const comic = id ? comicMap.get(id) : undefined;

  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter((c) => c.id !== comic.id && c.categorySlug === comic.categorySlug)
      .slice(0, 4);
  }, [comic]);

  if (!comic) {
    return (
      <>
        <SEO
          title="漫画未找到 - 51漫画网"
          description="抱歉，您要查看的漫画不存在或已下架。"
          keywords="韩国漫画,51漫画网"
        />
        <Navbar />
        <ScrollToTop />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-24 h-24 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-emotion-sad-line text-foreground-300 text-4xl"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">抱歉，您要查看的漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const displayedChapters = showAllChapters
    ? comic.chapters
    : comic.chapters.slice(0, 5);

  const detailSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: `${comic.title} - 韩国漫画免费阅读 - 51漫画网`,
      description: comic.description,
      author: {
        '@type': 'Person',
        name: comic.author,
      },
      datePublished: comic.publishedAt,
      dateModified: comic.updatedAt,
      publisher: {
        '@type': 'Organization',
        name: '51漫画网',
        url: 'https://www.51manhua.com',
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://www.51manhua.com/comic/${comic.id}`,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://www.51manhua.com',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: comic.category,
          item: `https://www.51manhua.com/category/${comic.categorySlug}`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: comic.title,
        },
      ],
    },
  ];

  return (
    <>
      <SEO
        title={`${comic.title} - 韩国漫画免费阅读 - 51漫画网`}
        description={`${comic.title}，${comic.author}作品。${comic.description.substring(0, 100)}... 51漫画网提供无删减韩国漫画下拉式阅读，${comic.category}分类韩漫免费畅读。`}
        keywords={`${comic.title},${comic.author},${comic.category},韩国漫画,无删减漫画,下拉式漫画,51漫画网,韩漫`}
        canonicalUrl={`https://www.51manhua.com/comic/${comic.id}`}
        ogType="article"
        ogImage={comic.coverImage}
        schemaData={detailSchema}
      />

      <Navbar />
      <ScrollToTop />

      <main className="pt-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: comic.category, to: `/category/${comic.categorySlug}` },
              { label: comic.title },
            ]}
          />

          {/* Comic Detail Header */}
          <article className="mt-8 flex flex-col md:flex-row gap-8">
            <div className="shrink-0 w-full md:w-[280px]">
              <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-100 relative">
                <img
                  src={comic.coverImage}
                  alt={`${comic.title} - 韩国漫画封面`}
                  title={`${comic.title} - 51漫画网韩漫推荐`}
                  className="w-full h-full object-cover object-top"
                />
                {comic.status === '连载中' && (
                  <span className="absolute top-3 left-3 px-3 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                    连载中
                  </span>
                )}
                {comic.status === '已完结' && (
                  <span className="absolute top-3 left-3 px-3 py-1 bg-accent-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                    已完结
                  </span>
                )}
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {comic.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-3">
                <span className="text-sm text-foreground-500 whitespace-nowrap">
                  作者：<strong className="text-foreground-700">{comic.author}</strong>
                </span>
                <span className="text-foreground-300 text-xs">|</span>
                <span className="text-sm text-foreground-500 whitespace-nowrap">
                  分类：
                  <Link
                    to={`/category/${comic.categorySlug}`}
                    className="text-primary-500 hover:text-primary-600 transition-colors"
                  >
                    {comic.category}
                  </Link>
                </span>
                <span className="text-foreground-300 text-xs">|</span>
                <span className="text-sm text-foreground-500 whitespace-nowrap">
                  状态：<strong className={comic.status === '连载中' ? 'text-primary-500' : 'text-accent-500'}>{comic.status}</strong>
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-4 mt-4">
                <div className="flex items-center gap-1.5">
                  <i className="ri-star-fill text-accent-400 text-base"></i>
                  <span className="text-lg font-bold text-foreground-900">{comic.rating}</span>
                  <span className="text-xs text-foreground-400">分</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-foreground-500">
                  <i className="ri-eye-line"></i>
                  <span>{(comic.views / 10000).toFixed(1)}万 阅读</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-foreground-500">
                  <i className="ri-heart-line"></i>
                  <span>{(comic.favorites / 10000).toFixed(1)}万 收藏</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-foreground-500">
                  <i className="ri-file-list-3-line"></i>
                  <span>共 {comic.totalChapters} 话</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-5 bg-background-100 rounded-xl p-5">
                <h2 className="font-heading text-sm font-semibold text-foreground-900 mb-2">
                  作品简介
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {comic.description}
                </p>
              </div>

              <div className="mt-5 flex flex-wrap gap-3">
                <Link
                  to={`/comic/${comic.id}/read/1`}
                  className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-full text-sm transition-all duration-300 whitespace-nowrap"
                >
                  开始阅读
                </Link>
                <button
                  type="button"
                  className="px-6 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 font-medium rounded-full text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-heart-line mr-1.5"></i>
                  收藏
                </button>
                <button
                  type="button"
                  className="px-6 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 font-medium rounded-full text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-share-forward-line mr-1.5"></i>
                  分享
                </button>
              </div>
            </div>
          </article>

          {/* Chapter List */}
          <section className="mt-12">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-xl font-bold text-foreground-900">
                章节列表
              </h2>
              <span className="text-xs text-foreground-400">
                共 {comic.totalChapters} 话 · 最近更新 {comic.updatedAt}
              </span>
            </div>
            <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
              <div className="divide-y divide-background-200/50">
                {displayedChapters.map((ch) => (
                  <Link
                    key={ch.id}
                    to={`/comic/${comic.id}/read/${ch.number}`}
                    className="flex items-center justify-between px-5 py-3.5 hover:bg-background-100 transition-colors group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors truncate">
                        {ch.title}
                      </span>
                    </div>
                    <span className="text-xs text-foreground-400 whitespace-nowrap ml-3">
                      {ch.updatedAt}
                    </span>
                  </Link>
                ))}
              </div>
              {comic.chapters.length > 5 && (
                <button
                  type="button"
                  onClick={() => setShowAllChapters(!showAllChapters)}
                  className="w-full py-3 text-sm text-primary-500 hover:text-primary-600 font-medium bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
                >
                  {showAllChapters ? '收起章节' : `查看全部 ${comic.totalChapters} 话`}
                </button>
              )}
            </div>
          </section>

          {/* Related Comics */}
          {relatedComics.length > 0 && (
            <section className="mt-14">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-bold text-foreground-900">
                  相关推荐
                </h2>
                <Link
                  to={`/category/${comic.categorySlug}`}
                  className="text-sm text-primary-500 hover:text-primary-600 transition-colors font-medium whitespace-nowrap"
                >
                  更多{comic.category}漫画
                  <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
                {relatedComics.map((rc) => (
                  <ComicCard key={rc.id} comic={rc} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}